// Typed client for fakturownia-sync and fakturownia-proxy edge functions.

import { BaseEdgeFnClient } from './BaseEdgeFnClient.ts';
import type { SupabaseClient } from '../base.ts';
import type { DryRunReport } from '../invoice/sync/types.ts';

export type { DryRunReport } from '../invoice/sync/types.ts';

// ── Sync types ──────────────────────────────────────────────────────────

export interface SyncItem {
  fakturowniaId: number;
  invoiceNumber: string;
  direction: 'received' | 'issued';
  action: 'created' | 'changes_detected' | 'unchanged' | 'skipped' | 'error';
  localInvoiceId?: number;
  sellerName: string | null;
  buyerName: string | null;
  grossAmount: number | null;
  currency: string;
  issueDate: string | null;
  changes?: Array<{ field: string; local_value: unknown; remote_value: unknown }>;
  warnings: string[];
  error?: string;
}

export interface SyncSummary {
  totalFetched: number;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  errors: number;
}

export interface SyncResult {
  summary: SyncSummary;
  items: SyncItem[];
  updatedSince: string;
}

// ── Attachment upload result ──────────────────────────────────────────────

/** Per-run report from the Cloud Run /attach-documents step. */
export interface AttachmentUploadReport {
  found: number;
  attached: number;
  already_present: number;
  /** Pending candidates still in scope after the pass; 0 = fully drained. */
  remaining: number;
  failed: number;
  failed_files?: Array<{ file_id: number; error: string }>;
  skipped_reason?: string;
}

// ── Fakturownia POST result ──────────────────────────────────────────────

/** Raw response from Fakturownia postInvoice API. */
export interface FakturowniaPostResult {
  id: number;
  number: string;
  [key: string]: unknown;
}

// ── Payment allocation resolution ─────────────────────────────────────────

/** Outcome of a reconcile-queue resolution action (reject / accept / link).
 *  `split_allocation` returns one of these per allocation at runtime — see
 *  {@link FakturowniaEdgeFnClient.splitAllocation}. */
export interface AllocationResolutionResult {
  allocationId: number;
  state: string;
  invoiceId: number | null;
}

// ── Per-invoice deep refresh ──────────────────────────────────────────────

/** Per-phase report from `/invoice/deep-refresh-inbound` — one entry per full-sweep
 *  phase, in the order the sweep runs them. A phase that failed non-fatally carries
 *  its `error` instead of counters, mirroring the sweep's own per-item stance. */
export interface DeepRefreshInboundReport {
  invoiceId: number | null;
  sync: { changed: boolean; revision: number; status: 'skipped' | 'touched' | 'updated' };
  // Union, not an optional field: a non-fatal phase failure returns ONLY `{error}`.
  // Callers must narrow — reading `findingsCreated` off a failed verify would render
  // "undefined" on a green step, which is the silent-failure class this replaced.
  verify: { status: string; findingsCreated: number } | { error: string };
  findings: { fixed: number; escalated: number; pending: number } | { error: string };
  projection: { projected: boolean; invoiceId: number | null; skipped: boolean };
  refs: { updated: boolean; error?: string };
  corrections: { attached: number; unresolved: number; error?: string };
  reclassify: { scanned: number; cleared: number; applied: number; dropped: number; error?: string };
}

/** Result of the bounded payments walk. `exhaustive` is false when the walk stopped
 *  at the page cap rather than reaching the end — the UI must say so. */
export interface BoundedPaymentsRefreshReport {
  pagesWalked: number;
  processed: number;
  changed: number;
  exhaustive: boolean;
  reconcile: Record<string, unknown>;
}

// ── Legacy types (used by old invoiceService.ts — remove when FE callers migrate) ──

export interface InvoiceGenerationResult {
  invoiceId?: number;
  invoiceNumber?: string;
  invoiceUrl?: string;
}

export interface InvoiceDateOverrides {
  issue_date: string;
  sell_date: string;
  payment_to: string;
  issue_place?: string;
}

// ── Client ──────────────────────────────────────────────────────────────

export class FakturowniaEdgeFnClient extends BaseEdgeFnClient {
  constructor(sb: SupabaseClient) {
    super(sb);
  }

  // ── fakturownia-sync actions ──────────────────────────────────────────

  /** Sync invoices from Fakturownia (fetch + process all at once). */
  async sync(invoiceIds?: number[], direction?: 'received' | 'issued'): Promise<SyncResult> {
    return this.callOrThrow<SyncResult>('fakturownia-sync', {
      action: 'sync', ...(invoiceIds ? { invoiceIds } : {}), ...(direction ? { direction } : {}),
    });
  }

  /** Backend sync — page-by-page with cursor saved between batches. */
  async syncBackend(direction: 'received' | 'issued'): Promise<SyncResult> {
    return this.callOrThrow<SyncResult>('fakturownia-sync', { action: 'sync_backend', direction });
  }

  /** Read-only preview: what a real sync would apply + what outbound cron would push.
   *  Always fetches all invoices from epoch — no cursor used. */
  async dryRun(direction: 'received' | 'issued'): Promise<DryRunReport> {
    return this.callOrThrow<DryRunReport>('fakturownia-sync', { action: 'dry_run', direction });
  }

  /** Push outbound changes now (sync-review "send"): dirty invoice statuses,
   *  pending attachments, and — when `includePayments` — pending payment links.
   *  `invoiceIds` scopes every pass to the selected invoices. */
  async syncDirty(opts: { invoiceIds?: number[]; includePayments?: boolean } = {}): Promise<{
    synced: number;
    failed: number;
    attachmentsUploaded: number;
    attachmentsFailed: number;
    paymentsPushed: number;
    paymentsFailed: number;
  }> {
    return this.callOrThrow('fakturownia-sync', {
      action: 'sync_dirty',
      ...(opts.invoiceIds?.length ? { invoiceIds: opts.invoiceIds } : {}),
      ...(opts.includePayments ? { includePayments: true } : {}),
    });
  }

  /** Reject a bogus FA payment allocation (sticky, checksum-pinned). */
  async rejectAllocation(allocationId: number): Promise<AllocationResolutionResult> {
    return this.callOrThrow<AllocationResolutionResult>('fakturownia-sync', {
      action: 'reject_allocation', allocationId,
    });
  }

  /** Accept an allocation as fa_only evidence. Requires the allocation to carry an
   *  allocated_amount (split a multi-invoice payment first) — else the edge fn 400s. */
  async acceptAllocation(allocationId: number): Promise<AllocationResolutionResult> {
    return this.callOrThrow<AllocationResolutionResult>('fakturownia-sync', {
      action: 'accept_allocation', allocationId,
    });
  }

  /** Manually link an allocation to an un-adopted bank line on its invoice family;
   *  adopts that link (marks it pushed with this payment id). */
  async linkAllocation(allocationId: number, linkId: number): Promise<AllocationResolutionResult> {
    return this.callOrThrow<AllocationResolutionResult>('fakturownia-sync', {
      action: 'link_allocation', allocationId, linkId,
    });
  }

  /** Split a multi-invoice payment across its allocations. Splits must cover ALL of
   *  the payment's allocations and sum to |payment.amount| ±0.01. Returns one
   *  result per allocation. */
  async splitAllocation(
    externalPaymentId: string,
    splits: Array<{ allocationId: number; amount: number }>,
  ): Promise<AllocationResolutionResult[]> {
    return this.callOrThrow<AllocationResolutionResult[]>('fakturownia-sync', {
      action: 'split_allocation', externalPaymentId, splits,
    });
  }

  /** Upload the pending annex7/ie599 attachments for one issued invoice.
   *  Runs in Cloud Run (Drive download) via gcp-bridge; idempotent through
   *  the fakturownia_attachments ledger. Returns the per-invoice upload report. */
  async uploadInvoiceAttachments(invoiceId: number): Promise<AttachmentUploadReport> {
    return this.callOrThrow<AttachmentUploadReport>('gcp-bridge', {
      action: 'attach_invoice_documents', invoice_id: invoiceId,
    });
  }

  /** Inbound half of the per-invoice deep refresh: force shadow sync (bypasses the
   *  checksum gate) → KSeF verify → findings reconcile → project → KSeF-reference and
   *  correction-parent backfill → conflict reclassify, all scoped to one invoice.
   *  `externalId` is the FAKTUROWNIA id (`invoices.external_system_id`), never the
   *  local `invoices.id` — they are different number spaces. */
  async refreshInvoiceInbound(
    externalId: string,
    direction: 'received' | 'issued',
  ): Promise<DeepRefreshInboundReport> {
    return this.callOrThrow<DeepRefreshInboundReport>('gcp-bridge', {
      action: 'refresh_invoice_inbound', external_id: externalId, direction,
    });
  }

  /** Pull recent Fakturownia payments into the read-model and reconcile allocations.
   *  NOT invoice-scoped — Fakturownia's `/banking/payments.json` cannot filter by
   *  invoice_id, so this walks the most recent `maxPages` pages (newest first) and
   *  reports `exhaustive:false` when it stopped at the cap. Never advances the
   *  payments cursor and never tombstones. */
  async refreshInvoicePayments(maxPages?: number): Promise<BoundedPaymentsRefreshReport> {
    return this.callOrThrow<BoundedPaymentsRefreshReport>('gcp-bridge', {
      action: 'refresh_invoice_payments', ...(maxPages != null ? { max_pages: maxPages } : {}),
    });
  }

  /** Run the app-side matcher (invoice ↔ transactions) for a single invoice. */
  async reconcileInvoiceMatches(
    invoiceId: number,
    direction: 'received' | 'issued',
  ): Promise<{ processed: number; hasMore: boolean }> {
    return this.callOrThrow<{ processed: number; hasMore: boolean }>('gcp-bridge', {
      action: 'reconcile_invoice_matches', invoice_id: invoiceId, direction,
    });
  }

  /** Trigger the full fakturownia-bidi-sync Cloud Workflow (inbound sync → reconcile →
   *  auto-approve → diff-check) on demand. Fire-and-forget: returns the started
   *  execution name; the sweep runs asynchronously in Cloud Workflows. */
  async triggerFullSync(): Promise<{ success: boolean; executionName: string }> {
    return this.callOrThrow<{ success: boolean; executionName: string }>('gcp-bridge', {
      action: 'trigger_bidi_sync',
    });
  }

  // ── fakturownia-proxy actions ─────────────────────────────────────────
  // Preparation + persistence now handled by shared InvoiceGenerationService.
  // Edge function only POSTs to Fakturownia API.

  /** POST an invoice payload to Fakturownia. Returns raw Fakturownia response. */
  async postInvoice(invoicePayload: Record<string, unknown>): Promise<FakturowniaPostResult> {
    return this.callOrThrow<FakturowniaPostResult>('fakturownia-proxy', {
      action: 'post_invoice', invoicePayload,
    });
  }

  /** POST an invoice + upload PDF attachment. Returns raw Fakturownia response. */
  async postInvoiceWithAttachment(
    invoicePayload: Record<string, unknown>,
    pdf: { base64: string; filename: string },
  ): Promise<FakturowniaPostResult> {
    return this.callOrThrow<FakturowniaPostResult>('fakturownia-proxy', {
      action: 'post_invoice_with_attachment', invoicePayload, pdfBase64: pdf.base64, pdfFilename: pdf.filename,
    });
  }

  // ── Legacy invoice generation (full pipeline via edge fn) ──────────────

  async generateInvoice(transactionIds: number[], side: string, dates?: { issue_date: string; sell_date: string; payment_to: string; issue_place?: string }): Promise<InvoiceGenerationResult> {
    return this.callOrThrow<InvoiceGenerationResult>('fakturownia-proxy', {
      action: 'generate', transactionIds, side, dates,
    });
  }

  async generateCorrectionInvoice(
    invoiceId: number, correctionReason: string,
    removedTransactionIds?: number[], dates?: Record<string, string>, splitPayment?: boolean,
  ): Promise<InvoiceGenerationResult> {
    return this.callOrThrow<InvoiceGenerationResult>('fakturownia-proxy', {
      action: 'generate_correction', invoiceId, correctionReason, removedTransactionIds, dates, splitPayment,
    });
  }

  async registerReceivedInvoice(invoiceData: Record<string, unknown>, pdfBase64?: string, pdfFilename?: string): Promise<InvoiceGenerationResult> {
    return this.callOrThrow<InvoiceGenerationResult>('fakturownia-proxy', {
      action: 'register_received', invoiceData, pdfBase64, pdfFilename,
    });
  }

  // ── Fakturownia client management ─────────────────────────────────────

  async syncClient(nip: string, companyData: { full_name: string; short_name: string; zip_code?: string | null; city?: string | null; street?: string | null; country_code?: string | null }): Promise<{ fakturowniaClientId: number; wasCreated: boolean }> {
    const searchResult = await this.call<Array<{ id?: number }>>('fakturownia-proxy', {
      endpoint: `/clients.json?tax_no=${encodeURIComponent(nip)}`, method: 'GET',
    });
    if (!searchResult.success) throw new Error('Fakturownia client search failed');
    const clients = Array.isArray(searchResult.data) ? searchResult.data : [];
    if (clients.length > 0 && typeof clients[0].id === 'number') {
      return { fakturowniaClientId: clients[0].id, wasCreated: false };
    }
    const createResult = await this.call<{ id?: number }>('fakturownia-proxy', {
      endpoint: '/clients.json', method: 'POST',
      body: { client: { name: companyData.full_name, shortcut: companyData.short_name, tax_no: nip, post_code: companyData.zip_code, city: companyData.city, street: companyData.street, country: companyData.country_code } },
    });
    if (!createResult.success || !createResult.data?.id) throw new Error('Fakturownia client creation failed');
    return { fakturowniaClientId: createResult.data.id, wasCreated: true };
  }
}
